import { isMcpConsentDocument, isMcpConsentPath } from "./mcpPrivacy";
/**
 * Bezoekherkenning via Snitcher.
 *
 * Snitcher herkent bedrijven normaal op IP-adres. Dat is een gok: bij een
 * klein autobedrijf op een consumentenlijn zit je er net zo vaak naast als
 * goed. Zodra iemand inlogt in dit portaal wéten we wie het is, en dan is
 * raden niet meer nodig — `identify` koppelt de sessie aan de gebruiker,
 * `group` aan de organisatie.
 *
 * Het `kvkNumber` in de group-payload gaat bewust mee: dat is de sleutel
 * waarmee een bezoek te herleiden is tot een debiteur in Lodder, zonder dat
 * daar een fuzzy match op bedrijfsnaam of e-maildomein aan te pas komt.
 *
 * Privacy: we sturen géén naam of e-mailadres van de gebruiker mee. Voor het
 * doel — weten wélke klant wat bekijkt — is de organisatie het relevante
 * niveau; de gebruiker heeft alleen een opaak id nodig om sessies aan elkaar
 * te knopen. Lees `identifyVisitor` voordat je hier traits aan toevoegt.
 */

const NAMESPACE = "Snitcher";
const CDN_HOST = "cdn.snitcher.com";
const API_HOST = "radar.snitcher.com";
const SCRIPT_ID = "__snitcher_radar__";

/**
 * Het id dat het officiële Snitcher-snippet zélf aan zijn script-element geeft.
 * Op denhartogbv.com wordt de tracker via Google Tag Manager geladen; dan staat
 * dit element er al. Twee trackers op één pagina betekent dubbele sessies en een
 * "Duplicate initialization"-waarschuwing, dus daar kijken we op.
 */
const OFFICIAL_SCRIPT_ID = "__radar__";

type SnitcherMethod = "identify" | "group" | "reset" | "page";

/**
 * Vóór het laden van de SDK is de global een array die aanroepen buffert;
 * daarna vervangt de SDK hem door een object met echte methodes. Beide vormen
 * moeten we aankunnen, want een gebruiker kan al ingelogd zijn voordat het
 * script binnen is.
 */
type SnitcherGlobal = Partial<Record<SnitcherMethod, (...args: unknown[]) => void>> & {
  initialized?: boolean;
  /** Gezet door het officiële snippet zodra het zichzelf heeft gebootstrapt. */
  _loaded?: boolean;
  push?: (entry: unknown[]) => void;
};

declare global {
  interface Window {
    Snitcher?: SnitcherGlobal;
  }
}

/** Leeg/afwezig id betekent: uit. Geen script, geen verkeer. */
export function getSnitcherProfileId(): string | null {
  if (isMcpConsentDocument || (typeof window !== "undefined" && isMcpConsentPath(window.location.pathname))) return null;
  const raw = import.meta.env.VITE_SNITCHER_PROFILE_ID;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed === "" ? null : trimmed;
}

export function isVisitorTrackingEnabled(): boolean {
  return getSnitcherProfileId() !== null;
}

function getQueue(): SnitcherGlobal | null {
  if (typeof window === "undefined") return null;
  const existing = window[NAMESPACE];
  if (existing) return existing;
  const queue = [] as unknown as SnitcherGlobal;
  window[NAMESPACE] = queue;
  return queue;
}

/**
 * Roept een SDK-methode aan, of buffert hem als het script nog niet geladen
 * is. Faalt nooit: bezoekherkenning mag het portaal niet kunnen breken.
 */
function call(method: SnitcherMethod, args: readonly unknown[]): void {
  if (!isVisitorTrackingEnabled()) return;
  try {
    const target = getQueue();
    if (!target) return;
    const fn = target[method];
    if (target.initialized === true && typeof fn === "function") {
      fn(...args);
      return;
    }
    if (typeof target.push === "function") {
      target.push([method, ...args]);
    }
  } catch {
    // Bewust stil: een mislukte tracking-aanroep is geen gebruikersprobleem.
  }
}

/**
 * Staat de tracker al op de pagina? Dat is het normale geval op sites waar
 * Google Tag Manager hem plaatst — daar mogen we hem níét nog eens laden.
 */
export function isTrackerAlreadyLoaded(): boolean {
  if (typeof document === "undefined") return false;
  if (document.getElementById(OFFICIAL_SCRIPT_ID) !== null) return true;
  if (document.getElementById(SCRIPT_ID) !== null) return true;
  const existing = typeof window === "undefined" ? undefined : window[NAMESPACE];
  return existing?.initialized === true || existing?._loaded === true;
}

/**
 * Laadt het tracking-script, maar alléén als niemand anders dat al doet.
 *
 * Tag Manager gaat vóór: staat de tracker er al, dan rijden we mee op die
 * instantie. `identify`/`group` schrijven immers gewoon naar dezelfde global,
 * of die nu door GTM of door ons is neergezet. Idempotent.
 */
export function initVisitorTracking(): void {
  const profileId = getSnitcherProfileId();
  if (profileId === null) return;
  if (typeof document === "undefined") return;
  if (isTrackerAlreadyLoaded()) return;

  try {
    getQueue();
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.type = "text/javascript";
    script.src = `https://${CDN_HOST}/releases/latest/radar.min.js`;
    script.setAttribute(
      "data-settings",
      JSON.stringify({
        apiEndpoint: API_HOST,
        cdn: CDN_HOST,
        namespace: NAMESPACE,
        profileId,
      }),
    );
    document.head.appendChild(script);
  } catch {
    // Zie `call`: nooit laten doorslaan naar de gebruiker.
  }
}

/**
 * Koppelt de huidige sessie aan een gebruiker.
 *
 * Alleen het opake gebruikers-id gaat mee. Voeg hier geen naam of e-mailadres
 * aan toe zonder dat dat AVG-technisch is vastgelegd — dit portaal bevat
 * klantmedewerkers, geen prospects.
 */
export function identifyVisitor(userId: string): void {
  if (userId === "") return;
  call("identify", [userId]);
}

export interface VisitorOrganizationTraits {
  readonly name: string;
  readonly kvkNumber: string | null;
  readonly organizationType: string;
}

/**
 * Koppelt de sessie aan een organisatie. Dit is het niveau waarop sales
 * stuurt: niet "iemand van Van Werven keek", maar "Van Werven keek".
 */
export function groupVisitor(organizationId: string, traits: VisitorOrganizationTraits): void {
  if (organizationId === "") return;
  const payload: Record<string, unknown> = {
    name: traits.name,
    organizationType: traits.organizationType,
  };
  // Alleen meesturen als hij er is — een lege sleutel maakt de match in Lodder
  // niet beter en vervuilt het bedrijfsprofiel in Snitcher.
  if (traits.kvkNumber !== null && traits.kvkNumber !== "") {
    payload.kvkNumber = traits.kvkNumber;
  }
  call("group", [organizationId, payload]);
}

/** Bij uitloggen: sessie losknopen van de gebruiker. */
export function resetVisitor(): void {
  call("reset", []);
}
